import { CancelOutlined, CheckCircleOutline, Visibility } from "@mui/icons-material";
import {
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from "@mui/material";
import React from "react";
import { t } from "ttag";
import Tooltip from "../../components/Tooltip/LightTooltip";
import { IDeviceHealthRecord } from "../../models/DeviceHealth.types";
import useStyles from "./DeviceHealthTable.style";
import { getHealthIssueSummary, hasHealthIssues } from "./deviceHealth.utils";

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

interface IDeviceHealthTableProps {
  records: IDeviceHealthRecord[];
  /** territory enum value -> label, resolved from the filter options */
  territoryNames: { [value: string]: string };
  /** shown instead of the rows when there are none */
  emptyText: string;
  totalCount: number;
  page: number;
  pageSize: number;
  loading: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onViewRecord: (record: IDeviceHealthRecord) => void;
}

const DeviceHealthTable: React.FC<IDeviceHealthTableProps> = ({
  records,
  territoryNames,
  emptyText,
  totalCount,
  page,
  pageSize,
  loading,
  onPageChange,
  onPageSizeChange,
  onViewRecord
}) => {
  const classes: any = useStyles();

  const handlePageChange = (_event: unknown, newPage: number) => {
    onPageChange(newPage);
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onPageSizeChange(parseInt(event.target.value, 10));
  };

  const handleViewClick = (record: IDeviceHealthRecord) => () => {
    onViewRecord(record);
  };

  const renderTerritory = (record: IDeviceHealthRecord) => {
    if (record.salesTerritory === undefined || record.salesTerritory === null) {
      return "-";
    }

    const value = String(record.salesTerritory);

    return territoryNames[value] || value;
  };

  const renderHealthIndicator = (record: IDeviceHealthRecord) => {
    const issues = getHealthIssueSummary(record);

    if (!hasHealthIssues(record)) {
      return (
        <Tooltip title={t`No issues detected`} placement="top">
          <CheckCircleOutline className={classes.healthyIcon} />
        </Tooltip>
      );
    }

    return (
      <Tooltip title={issues.join("\n")} placement="top">
        <CancelOutlined className={classes.unhealthyIcon} />
      </Tooltip>
    );
  };

  const renderRows = () => {
    if (!records.length) {
      return (
        <TableRow>
          <TableCell colSpan={6} className={classes.emptyCell}>
            <Typography className={classes.emptyText}>{emptyText}</Typography>
          </TableCell>
        </TableRow>
      );
    }

    return records.map((record) => (
      <TableRow hover key={record.id} className={classes.row}>
        <TableCell className={classes.cell}>{renderTerritory(record)}</TableCell>
        <TableCell className={classes.cell}>{record.customer?.name || "-"}</TableCell>
        <TableCell className={classes.cell}>{record.site?.name || "-"}</TableCell>
        <TableCell className={classes.cell}>{record.serial}</TableCell>
        <TableCell align="center" className={classes.cell}>
          {renderHealthIndicator(record)}
        </TableCell>
        <TableCell align="center" className={classes.cell}>
          <Tooltip title={t`View device health record`} placement="top">
            <IconButton size="small" onClick={handleViewClick(record)}>
              <Visibility className={classes.viewIcon} />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <Paper elevation={0} className={classes.tablePaper}>
      <TableContainer className={classes.tableContainer}>
        {loading && (
          <div className={classes.loadingOverlay}>
            <CircularProgress size={32} />
          </div>
        )}
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.headCell}>{t`SALES TERRITORY`}</TableCell>
              <TableCell className={classes.headCell}>{t`CUSTOMER NAME`}</TableCell>
              <TableCell className={classes.headCell}>{t`SITE NAME`}</TableCell>
              <TableCell className={classes.headCell}>{t`DEVICE SERIAL`}</TableCell>
              <TableCell align="center" className={classes.headCell}>{t`HEALTH INDICATOR`}</TableCell>
              <TableCell align="center" className={classes.headCell}>{t`VIEW`}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{renderRows()}</TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        className={classes.pagination}
        count={totalCount}
        page={page}
        rowsPerPage={pageSize}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handlePageSizeChange}
      />
    </Paper>
  );
};

export default DeviceHealthTable;
